import { useEffect, useRef, useState } from "react";

interface UseConfirmedEntityIdOptions {
    /** Id the URL asks for */
    id: string | undefined;
    isDirty: boolean;
    /** Resolves true when the user agrees to lose the unsaved changes */
    confirmDiscard: () => Promise<boolean>;
    /** The user kept the unsaved changes: put the previous id back, e.g. into the URL */
    onReject: (confirmedId: string) => void;
}

/**
 * The id whose editor is actually shown. It follows `id`, except when `id` switches to another entity while there
 * are unsaved changes: then the current one stays until the user confirms the discard (or `onReject` restores it).
 * Closing (`id` cleared) is not guarded here, that goes through the drawer's own `onBeforeClose`.
 */
export const useConfirmedEntityId = ({ id, isDirty, confirmDiscard, onReject }: UseConfirmedEntityIdOptions) => {
    const [confirmedId, setConfirmedId] = useState(id);

    // The effect below must only re-run when `id` changes, so the rest is read through refs
    const latest = useRef({ isDirty, confirmDiscard, onReject, confirmedId });
    latest.current = { isDirty, confirmDiscard, onReject, confirmedId };

    useEffect(() => {
        const { isDirty: dirty, confirmedId: current } = latest.current;
        if (id === current) {
            return;
        }
        if (!id || !current || !dirty) {
            setConfirmedId(id);
            return;
        }

        let stale = false;
        latest.current.confirmDiscard().then((discard) => {
            if (stale) {
                return;
            }
            if (discard) {
                setConfirmedId(id);
            } else {
                latest.current.onReject(current);
            }
        });
        return () => {
            stale = true;
        };
    }, [id]);

    return confirmedId;
};
